import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  X,
  Mail,
  Phone,
  ExternalLink,
  MapPin,
  HardHat,
  Cog,
  Package,
  DollarSign,
  Clock,
  Building2,
  Star,
  Wrench,
} from "lucide-react";

/**
 * Supplier 360° detail dialog — one-tap view of everything tied to a single
 * supplier: contact + website, technicians on staff, machines they service,
 * inventory sourced, 90d spend, and average call-out response time.
 *
 * Backed by GET /api/suppliers/{sid}/detail. Read-only; use the pencil on
 * the Suppliers list card to edit the supplier's core fields.
 */
const SupplierDetailDialog = ({ open, supplierId, onClose, onEdit, onAddTechnician, onLinkMachine }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !supplierId) {
      setData(null);
      return;
    }
    setBusy(true);
    api
      .get(`/suppliers/${supplierId}/detail`, { params: { days: 90 } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [open, supplierId]);

  if (!open) return null;
  const s = data?.supplier;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="supplier-detail-overlay"
      onClick={onClose}
    >
      <div
        className="nb-card bg-white max-w-4xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="supplier-detail-dialog"
      >
        <div className="flex items-start justify-between p-5 border-b-2 border-black gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow flex items-center gap-2">
              <Building2 size={12} strokeWidth={2.5} /> Supplier · 360° view
            </div>
            <h2 className="font-display text-3xl leading-tight mt-1">
              {s?.name || (busy ? "Loading…" : "Not found")}
            </h2>
            {s?.contact_name && (
              <div className="text-sm text-[#607087] mt-1">{s.contact_name}</div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {onEdit && s && (
              <button
                data-testid="supplier-detail-edit-btn"
                onClick={() => onEdit(s)}
                className="nb-btn bg-white px-3 py-2 text-sm"
              >
                Edit
              </button>
            )}
            <button
              data-testid="supplier-detail-close"
              onClick={onClose}
              className="nb-btn bg-white p-2"
              aria-label="close"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {s && (
          <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Contact strip */}
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {s.phone && (
                <a
                  href={`tel:${s.phone}`}
                  data-testid="supplier-detail-phone"
                  className="nb-btn bg-[#EAF5FB] px-3 py-2 flex items-center gap-2 text-sm font-bold"
                >
                  <Phone size={14} strokeWidth={2.5} className="text-[#228EBF]" />
                  <span className="truncate">{s.phone}</span>
                </a>
              )}
              {s.email && (
                <a
                  href={`mailto:${s.email}`}
                  data-testid="supplier-detail-email"
                  className="nb-btn bg-white px-3 py-2 flex items-center gap-2 text-sm font-bold"
                >
                  <Mail size={14} strokeWidth={2.5} />
                  <span className="truncate">{s.email}</span>
                </a>
              )}
              {s.website && (
                <a
                  href={
                    /^https?:\/\//i.test(s.website) ? s.website : `https://${s.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="supplier-detail-website"
                  className="nb-btn bg-[#FFF9EC] px-3 py-2 flex items-center gap-2 text-sm font-bold text-[#7A4A00]"
                >
                  <ExternalLink size={14} strokeWidth={2.5} />
                  <span className="truncate">Order online</span>
                </a>
              )}
              {s.address && (
                <div className="border-2 border-black rounded-lg px-3 py-2 flex items-center gap-2 text-sm">
                  <MapPin size={14} strokeWidth={2.5} />
                  <span className="truncate">{s.address}</span>
                </div>
              )}
            </div>

            {/* Metric tiles */}
            <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="nb-card-sm p-3">
                <div className="label-eyebrow flex items-center gap-1">
                  <HardHat size={11} strokeWidth={2.5} /> Techs
                </div>
                <div className="font-display text-2xl mt-1" data-testid="supplier-detail-tech-count">
                  {data.technicians.length}
                </div>
              </div>
              <div className="nb-card-sm p-3">
                <div className="label-eyebrow flex items-center gap-1">
                  <Cog size={11} strokeWidth={2.5} /> Machines
                </div>
                <div className="font-display text-2xl mt-1" data-testid="supplier-detail-machine-count">
                  {data.machines.length}
                </div>
              </div>
              <div className="nb-card-sm p-3">
                <div className="label-eyebrow flex items-center gap-1">
                  <Package size={11} strokeWidth={2.5} /> Items
                </div>
                <div className="font-display text-2xl mt-1" data-testid="supplier-detail-item-count">
                  {data.stock_items.length}
                </div>
              </div>
              <div className="nb-card-sm p-3 bg-[#DFEEF7] border-[#0A3A7A]">
                <div className="label-eyebrow flex items-center gap-1">
                  <DollarSign size={11} strokeWidth={2.5} /> {data.days}d spend
                </div>
                <div className="font-display text-xl mt-1" data-testid="supplier-detail-spend">
                  ${data.spend.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div className="text-[10px] text-[#607087]">
                  {data.spend.movement_count} received
                </div>
              </div>
            </div>

            {/* Toll-processing block — visible only for processors
                (JV Strappings etc.) whose spend comes from companion POs.
                Placed above the technicians/machines split so the manager
                sees the cutting spend before scrolling. */}
            {(data.toll_processing?.po_count || 0) > 0 && (
              <div
                data-testid="supplier-detail-toll-processing"
                className="lg:col-span-3 nb-card-sm p-3 bg-[#FFF8E1] border-[#7A5300]"
              >
                <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
                  <div className="flex items-center gap-2">
                    <Wrench size={14} strokeWidth={2.5} className="text-[#7A5300]" />
                    <div className="label-eyebrow text-[#7A5300]">
                      Toll processing · {data.days}d
                    </div>
                  </div>
                  <div className="font-mono-num font-bold text-lg text-[#3B2A00]">
                    ${data.toll_processing.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    <span className="text-xs font-normal text-[#7A5300] ml-2">
                      across {data.toll_processing.po_count} PO
                      {data.toll_processing.po_count === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
                {data.toll_processing.pos.length > 0 && (
                  <ul
                    className="flex flex-col gap-1 max-h-56 overflow-y-auto"
                    data-testid="supplier-detail-toll-processing-list"
                  >
                    {data.toll_processing.pos.map((p) => (
                      <li
                        key={p.id}
                        className="border-2 border-[#7A5300] rounded-lg px-2 py-1.5 bg-[#FFFCF2] text-xs flex items-center gap-2 flex-wrap"
                        data-testid={`supplier-detail-toll-po-${p.id}`}
                      >
                        <span className="font-display text-sm">{p.po_number}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#7A5300] bg-white uppercase font-bold tracking-wider text-[#7A5300]">
                          {p.status}
                        </span>
                        <span className="font-mono-num">
                          {p.qty} {p.unit} × ${p.unit_cost.toFixed(2)}
                        </span>
                        <span className="ml-auto font-mono-num font-bold">
                          ${p.line_total.toFixed(2)}
                        </span>
                        {p.parent_po_number && (
                          <div className="basis-full text-[10px] text-[#7A5300]">
                            Linked to <b>{p.parent_po_number}</b>
                            {p.parent_supplier_name && ` · ${p.parent_supplier_name}`}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Call-out response */}
            {data.callouts.count > 0 && (
              <div className="lg:col-span-3 nb-card-sm p-3 bg-[#FCE4E4] border-[#8A1F1F]">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={14} strokeWidth={2.5} className="text-[#8A1F1F]" />
                  <div className="label-eyebrow text-[#8A1F1F]">
                    Call-out response · avg{" "}
                    <span className="font-bold">
                      {data.callouts.avg_response_hours}h
                    </span>{" "}
                    across {data.callouts.count} incident
                    {data.callouts.count === 1 ? "" : "s"} (last {data.days}d)
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {data.callouts.incidents.slice(0, 6).map((inc, i) => (
                    <span
                      key={i}
                      className="text-xs px-2 py-0.5 border-2 border-[#8A1F1F] rounded bg-white font-mono-num"
                    >
                      {inc.machine_name}: {inc.response_hours}h
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Technicians column */}
            <div className="border-2 border-black rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow flex items-center gap-1.5">
                  <HardHat size={12} strokeWidth={2.5} /> Technicians
                </div>
                {onAddTechnician && (
                  <button
                    data-testid="supplier-detail-add-tech-btn"
                    onClick={() => onAddTechnician(s)}
                    className="nb-btn bg-white px-2 py-0.5 text-[10px] flex items-center gap-1"
                    title={`Add a new technician for ${s.name}`}
                  >
                    <HardHat size={10} strokeWidth={2.5} /> Add
                  </button>
                )}
              </div>
              {data.technicians.length === 0 ? (
                <div className="text-xs text-[#607087]">
                  None on staff yet — add on the Technicians page.
                </div>
              ) : (
                <ul className="flex flex-col gap-1.5" data-testid="supplier-detail-techs">
                  {data.technicians.map((t) => (
                    <li
                      key={t.id}
                      className="border-2 border-black rounded-lg px-2 py-1.5 bg-white text-sm"
                      data-testid={`supplier-detail-tech-${t.id}`}
                    >
                      <div className="font-bold">{t.name}</div>
                      {t.role && (
                        <div className="text-[10px] text-[#607087]">{t.role}</div>
                      )}
                      <div className="flex flex-wrap gap-2 mt-1">
                        {t.phone && (
                          <a
                            href={`tel:${t.phone}`}
                            className="text-[11px] font-bold text-[#228EBF] hover:underline flex items-center gap-1"
                          >
                            <Phone size={10} strokeWidth={2.5} /> {t.phone}
                          </a>
                        )}
                        {t.email && (
                          <a
                            href={`mailto:${t.email}`}
                            className="text-[11px] hover:underline flex items-center gap-1"
                          >
                            <Mail size={10} strokeWidth={2.5} /> {t.email}
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Machines column */}
            <div className="border-2 border-black rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow flex items-center gap-1.5">
                  <Cog size={12} strokeWidth={2.5} /> Machines serviced
                </div>
                {onLinkMachine && data.technicians.length > 0 && (
                  <button
                    data-testid="supplier-detail-link-machine-btn"
                    onClick={() => onLinkMachine(s, data.technicians)}
                    className="nb-btn bg-white px-2 py-0.5 text-[10px] flex items-center gap-1"
                    title="Link one of this supplier's techs to a machine"
                  >
                    <Cog size={10} strokeWidth={2.5} /> Link
                  </button>
                )}
              </div>
              {data.machines.length === 0 ? (
                <div className="text-xs text-[#607087]">
                  No machines yet — link this supplier's techs to a machine on
                  the Machinery page (or the Technicians page).
                </div>
              ) : (
                <ul className="flex flex-col gap-1.5" data-testid="supplier-detail-machines">
                  {data.machines.map((m) => (
                    <li
                      key={m.id}
                      className="border-2 border-black rounded-lg px-2 py-1.5 bg-white text-sm"
                      data-testid={`supplier-detail-machine-${m.id}`}
                    >
                      <div className="font-bold flex items-center gap-1.5">
                        {m.status &&
                          ["broken", "broken_unconfirmed"].includes(m.status) && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#E5263F] text-white font-bold">
                              BROKEN
                            </span>
                          )}
                        {m.name}
                      </div>
                      {m.category && (
                        <div className="text-[10px] text-[#607087]">
                          {m.category} · {m.department || "—"}
                        </div>
                      )}
                      {m.serviced_by_techs?.length > 0 && (
                        <div className="text-[10px] mt-1 flex items-center gap-1 flex-wrap">
                          <Star size={9} strokeWidth={2.5} className="text-[#FFB300]" />
                          <span className="text-[#607087]">
                            {m.serviced_by_techs.join(", ")}
                          </span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Stock items column */}
            <div className="border-2 border-black rounded-xl p-3">
              <div className="label-eyebrow flex items-center gap-1.5 mb-2">
                <Package size={12} strokeWidth={2.5} /> Inventory sourced
              </div>
              {data.stock_items.length === 0 ? (
                <div className="text-xs text-[#607087]">
                  Nothing sourced from this supplier yet — tag items on
                  Inventory.
                </div>
              ) : (
                <ul className="flex flex-col gap-1 max-h-[300px] overflow-y-auto" data-testid="supplier-detail-items">
                  {data.stock_items.map((it) => (
                    <li
                      key={it.id}
                      className="text-sm flex items-center justify-between gap-2 py-1 border-b border-[#EEE] last:border-0"
                      data-testid={`supplier-detail-item-${it.id}`}
                    >
                      <span className="truncate flex-1 min-w-0">
                        <span className="font-bold">{it.name}</span>
                        {it.category && (
                          <span className="text-[10px] text-[#607087] ml-1">
                            · {it.category}
                          </span>
                        )}
                      </span>
                      <span className="font-mono-num text-xs whitespace-nowrap">
                        {it.current_qty ?? 0} {it.unit || ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {s.notes && (
              <div className="lg:col-span-3 border-2 border-black rounded-lg p-3 bg-[#FFF9EC] text-sm whitespace-pre-wrap">
                <div className="label-eyebrow mb-1">Notes</div>
                {s.notes}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierDetailDialog;
